import React, { useState, useMemo, useCallback } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { AlreadyActiveUnitWithName, appAgentService } from '../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import { ClientError, InvalidValue } from '../../../services/central-api/base-service';
import moment from 'moment';
import { useStyles, createStyleSheet } from 'react-native-unistyles';
import FinishUnitModal from './FinishUnitModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faEllipsisVertical, faExclamationCircle, faPause, faPlay, faUserGear, faUsersSlash, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import MyDataTable from '../../../components/MyDataTable';
import EditAgentModal from './EditAgentModal';
import EditEquipmentModal from './EditEquipmentModal';
import { ScrollView } from 'react-native-web-hover';
import OperationalPauseModal from './OperationalPauseModal';
import { useAuth } from '../../../context/Auth';
import { minutesToText } from '../../../services/time-helpers';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import FloatingButton from '../../../components/FloatingButton';
import ChangeUnitRoleModal, { UnitRole } from './ChangeUnitRoleModal';
import CustomButton from '../../../components/CustomButton';

export default function AgentUnits() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [units, setUnits] = useState<AppAgentUnit[]>([]);
    const [unitId, setUnitId] = useState<number>(0);
    const [unit, setUnit] = useState<AppAgentUnit>();
    const [isPersonInCharge, setIsPersonInCharge] = useState<boolean>();
    const [unitPickerOpen, setUnitPickerOpen] = useState(false);
    const [needEarlyFinishReason, setNeedEarlyFinishReason] = useState(false);
    const [finishModal, setFinishModal] = useState(false);
    const [finishEarlyReasonId, setFinishEarlyReasonId] = useState<number>(0);
    const [alreadyActive, setAlreadyActive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [dailyPoliceLog, setDailyPoliceLog] = useState<string>('');
    const [observations, setObservations] = useState<string>('');

    const [selectedAgentId, setSelectedAgentId] = useState<number>(0);
    const [editAgentModal, setEditAgentModal] = useState<boolean>(false);
    const [activationType, setActivationType] = useState<'teamActivation' | 'agentActivation' | 'agentEdit'>('agentActivation');

    const [selectedEquipmentId, setSelectedEquipmentId] = useState<number>(0);
    const [editEquipmentModal, setEditEquipmentModal] = useState<boolean>(false);
    const [unitRoleModal, setUnitRoleModal] = useState<UnitRole | null>(null);

    const [operationalPauseModal, setOperationalPauseModal] = useState<boolean>(false);
    const [pauseReason, setPauseReason] = useState<number>(0);

    async function getPageInformation() {
        try {
            setIsLoading(true);
            const agentActiveUnit = await appAgentService.getAgentActiveUnit();
            if (agentActiveUnit) {
                setUnitId(agentActiveUnit.id);
                for (const agent of agentActiveUnit.UnitUsers) {
                    agent.active = agent.unitId == agentActiveUnit.id;
                }
                for (const equipment of agentActiveUnit.equipments) {
                    equipment.active = equipment.unitId == agentActiveUnit.id;
                }
                setUnit(agentActiveUnit);
                setUnits([agentActiveUnit]);
                setAlreadyActive(true);

                const personInChargeUser = agentActiveUnit.UnitUsers?.find(uu => uu.UnitUser.isPersonInCharge);
                setIsPersonInCharge(personInChargeUser?.id === authenticatedUser?.id);
            } else {
                setAlreadyActive(false);
                setUnitId(0);
                setUnit(undefined);
                setUnits([]);
                const resp = await appAgentService.getAgentUnits();
                setUnits(resp);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function restoreUnitFromLoadedData() {
        const originalUnit = units.find(u => u.id === unitId);
        if (originalUnit) {
            setUnit(originalUnit);
        }
    }

    async function setActiveUnit(extra?: { driverId?: number; personInChargeId?: number; }) {
        try {
            if (!unit) {
                return;
            }

            await appAgentService.setAgentActiveUnit({
                unitId,
                agents: unit.UnitUsers.map((user) => {
                    return {
                        id: user.id,
                        active: user.active,
                        assignmentId: user.UnitUser.assignmentId ? user.UnitUser.assignmentId : undefined,
                        finishEarlyReasonId: !user.active && user.UnitUser.unitFinishEarlyReasonId ? user.UnitUser.unitFinishEarlyReasonId : undefined,
                        agentScheduleId: user.UnitUser.agentScheduleId ? user.UnitUser.agentScheduleId : undefined
                    };
                }),
                equipments: unit.equipments.map((equipment) => {
                    return {
                        id: equipment.id,
                        active: !extra && equipment.id === selectedEquipmentId ? false : equipment.active,
                        reason: equipment.reason
                    };
                }),
                ...extra,
            });
            await getPageInformation();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            return true;
        } catch (err) {
            restoreUnitFromLoadedData();
            if (err instanceof AlreadyActiveUnitWithName) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message, { actingBody: err.actingBody }),
                    visibilityTime: 10000,
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            } else if (err instanceof InvalidValue) {
                if (err.field === 'driverId') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('driverMustBeChanged'),
                    });
                } else if (err.field === 'personInChargeId') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('personInChargeMustBeChanged'),
                    });
                } else if (err.field?.startsWith('checklist')) {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidChecklist'),
                    });
                } else if (err.field === 'agents') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('responsibleMustBeActive'),
                    });
                }
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    async function checkResetUnit() {
        const unitToLeave = units.find(unit => unit.id == unitId);
        if (unitToLeave && moment(unitToLeave.estimatedFinish).toDate().getTime() > moment().toDate().getTime()) {
            setNeedEarlyFinishReason(true);
        }

        setFinishModal(true);
    }

    async function resetUnit() {
        try {
            await appAgentService.resetAgentActiveUnit({
                dailyPoliceLog: dailyPoliceLog,
                observations: observations !== '' ? observations : undefined,
                finishEarlyReasonId: finishEarlyReasonId != 0 ? finishEarlyReasonId : undefined,
            });
            await getPageInformation();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('SUCCESS_FINISH_UNIT_FOR_AGENT'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            } else if (err instanceof InvalidValue && err.field === 'checklist') {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('invalidChecklist'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }

    }

    function alertLateActivatedUnit() {
        if (!unit || unit.OccurrenceUnits.length) {
            return null;
        }

        const timeDiff = moment().diff(moment(unit.estimatedFinish), 'minutes');

        if (timeDiff < 0) {
            return null;
        }

        const message = timeDiff > 0 && timeDiff <= 30
            ? translate('lateActivatedUnitMessage', { minutes: 30 - timeDiff })
            : translate('lateActivatedUnitMessageWithoutTime');

        return (
            <View style={styles.lateActivatedBox}>
                <FontAwesomeIcon icon={faExclamationCircle} fontSize={22} color='#B8401B' />
                <MyAppText style={styles.lateActivatedText}>{message}</MyAppText>
            </View>
        );
    }

    async function unpauseUnit() {
        try {
            if (unit && unit.paused) {
                await appAgentService.pauseUnpauseUnit(unit.id, 'unpause');
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                getPageInformation();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getPageInformation();
        }
    }, [isFocused]));

    function getAgentStatus(agent: UserAgent) {
        if (agent.unitId !== null && agent.unitId !== unit?.id) {
            return <FontAwesomeIcon icon={faExclamationTriangle as IconDefinition} color={theme.colors.dataTable.alert} />;
        }
        return <View style={[styles.circle, { backgroundColor: agent.active ? theme.colors.successes['300'] : theme.colors.errors['300'] }]}></View>;
    }

    const { isInOverdue, minutesInOverdue } = useMemo(() => {
        if (unit && unit.paused && unit.unitPauseReason?.pauseMinutes) {
            const diff = moment().diff(unit.pause_start, 'minutes') - unit.unitPauseReason.pauseMinutes;
            return { isInOverdue: diff > 0, minutesInOverdue: Math.max(diff, 0) };
        }
        return { isInOverdue: false, minutesInOverdue: 0 };
    }, [unit]);

    const unitPickerItems = useMemo(() => units.map((item: AppAgentUnit) => ({
        label: `${item.unitName.name} - ${moment(item.estimatedStart).format('DD/MM/YYYY HH:mm')} - ${moment(item.estimatedFinish).format('DD/MM/YYYY HH:mm')}`,
        value: item.id
    })), [units]);

    return (
        <View style={{ flex: 1, padding: 16 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                <MyAppText style={styles.title}>{translate('teams')}</MyAppText>
                {isPersonInCharge && <MyAppText style={styles.subTitle}>{translate('personInCharge')}</MyAppText>}
            </View>
            <View style={styles.divider} />
            <View style={styles.groupContainer}>
                <View style={styles.pageHeader}>
                    <MyAppText style={{ fontSize: 16, fontWeight: '600', color: theme.colors.primaryButton.background }}>
                        {translate('team')}
                    </MyAppText>
                    {unit?.paused ?
                        <View style={{ flexDirection: 'column' }}>
                            <View style={{ flexDirection: 'row', gap: 4 }}>
                                <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primaryButton.background }}>
                                    {isInOverdue ? translate('overdue') : translate('onPause')}
                                </MyAppText>
                                {isInOverdue &&
                                    <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primaryButton.background }}>
                                        -
                                    </MyAppText>
                                }
                                {isInOverdue &&
                                    <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.errors['300'] }}>
                                        {`${minutesToText(minutesInOverdue)}`}
                                    </MyAppText>
                                }
                            </View>
                            <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.emphasisBlue }}>
                                {unit.unitPauseReason?.name ?? ''}
                            </MyAppText>
                        </View>
                        : <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primaryButton.background }}>
                            {translate('inActivity')}
                        </MyAppText>
                    }
                </View>
                <View style={styles.cardList}>
                    {isLoading ? <ActivityIndicator size='large' style={styles.loadingContainer} color={theme.colors.dataTable.loading} />
                        : <ScrollView contentContainerStyle={{ rowGap: 32, zIndex: 2 }}>
                            {alertLateActivatedUnit()}
                            <View style={{ zIndex: 3, rowGap: 8 }}>
                                <MyAppText style={{ fontSize: 14, fontWeight: '600' }}>{translate('team')}</MyAppText>
                                <MyDropDownPicker
                                    open={unitPickerOpen}
                                    setOpen={setUnitPickerOpen}
                                    value={unitId}
                                    setValue={(value) => {
                                        const selectedUnitId = value(unitId);
                                        if (selectedUnitId === unitId) {
                                            return;
                                        }
                                        setUnitId(selectedUnitId);
                                        const foundUnit = units.find(unit => unit.id == selectedUnitId);
                                        if (foundUnit) {
                                            for (const agent of foundUnit.UnitUsers) {
                                                agent.active = agent.unitId == foundUnit.id;
                                            }
                                            for (const equipment of foundUnit.equipments) {
                                                equipment.active = equipment.unitId == foundUnit.id;
                                            }
                                            setUnit(foundUnit);
                                        }
                                    }}
                                    items={unitPickerItems}
                                    dropDownDirection='BOTTOM'
                                    searchable={true}
                                    disabled={alreadyActive}
                                />
                                {unit && !unit.definitiveStart && !alreadyActive && unit.UnitUsers.some(uu => uu.id === authenticatedUser?.id) && <CustomButton
                                    onPress={() => {
                                        setSelectedAgentId(authenticatedUser!.id);
                                        setActivationType('teamActivation');
                                        setEditAgentModal(true);
                                    }}
                                    text={translate('activateTeam')}
                                />}
                            </View>

                            <View style={styles.tableCard}>
                                <MyAppText style={styles.tableTitle}>{translate('availableAgents')}</MyAppText>
                                {unitId !== 0 ?
                                    <MyDataTable
                                        useHighlightedHeader
                                        useMinimalTable
                                        onRowClicked={(row) => {
                                            if (unit?.definitiveStart) {
                                                setSelectedAgentId(row.id);
                                                setActivationType(row.active ? 'agentEdit' : 'agentActivation');
                                                setEditAgentModal(true);
                                            }
                                        }}
                                        columns={[{
                                            maxWidth: '16px',
                                            minWidth: '16px',
                                            center: true,
                                            wrap: true,
                                            cell: row => getAgentStatus(row)
                                        },
                                        {
                                            name: translate('name'),
                                            style: styles.tableHeader(125),
                                            wrap: true,
                                            cell: row => <View style={{ maxWidth: '100%', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                                <MyAppText numberOfLines={1} ellipsizeMode="tail" style={styles.tableRowContent}>{row.warName || row.name}</MyAppText>
                                                {row.UnitUser.isDriver && <FontAwesomeIcon icon={faCar} fontSize={16} color={theme.colors.iconColor} />}
                                                {row.UnitUser.isPersonInCharge && <FontAwesomeIcon icon={faUserGear} fontSize={16} color={theme.colors.iconColor} />}
                                            </View>
                                        },
                                        {
                                            name: translate('registry'),
                                            style: styles.tableHeader(75),
                                            cell: row => <View style={{ maxWidth: '100%' }}>
                                                <MyAppText numberOfLines={1} ellipsizeMode="tail" style={styles.tableRowContent}>{row.registry}</MyAppText>
                                            </View>,
                                            wrap: true,
                                        },
                                        {
                                            style: { justifyContent: 'end', paddingRight: 16 },
                                            button: true,
                                            maxWidth: '40px',
                                            minWidth: '40px',
                                            omit: !unit?.definitiveStart,
                                            cell: row =>
                                                <TouchableOpacity
                                                    style={{ paddingHorizontal: 8, paddingVertical: 6, borderRadius: 9999 }}
                                                    onPress={() => {
                                                        setSelectedAgentId(row.id);
                                                        setActivationType(row.active ? 'agentEdit' : 'agentActivation');
                                                        setEditAgentModal(true);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEllipsisVertical} fontSize={20} color={theme.colors.iconColor} />
                                                </TouchableOpacity>
                                        }]}
                                        data={units.find(unit => unit.id == unitId)?.UnitUsers || []}
                                    /> :
                                    <MyAppText style={styles.tableNoContentSubtitle}>{translate('noAvailableAgents')}</MyAppText>}
                            </View>
                            <View style={styles.tableCard}>
                                <MyAppText style={styles.tableTitle}>{translate('availableEquipments')}</MyAppText>
                                {unitId !== 0 && units.find(unit => unit.id == unitId)?.equipments.length ?
                                    <MyDataTable
                                        useHighlightedHeader
                                        useMinimalTable
                                        onRowClicked={(row) => {
                                            setSelectedEquipmentId(row.id);
                                            setEditEquipmentModal(true);
                                        }}
                                        columns={[{
                                            name: translate('name'),
                                            style: { fontSize: 14, fontWeight: '400', color: theme.colors.dataTable.rowText },
                                            selector: row => row.name,
                                            wrap: true,
                                            grow: 1
                                        },
                                        {
                                            style: { justifyContent: 'end', paddingRight: 16 },
                                            button: true,
                                            cell: row =>
                                                <TouchableOpacity onPress={() => {
                                                    setSelectedEquipmentId(row.id);
                                                    setEditEquipmentModal(true);
                                                }}>
                                                    <FontAwesomeIcon icon={faEllipsisVertical} fontSize={20} color={theme.colors.iconColor} />
                                                </TouchableOpacity>
                                        }]}
                                        data={units.find(unit => unit.id == unitId)?.equipments.filter(eq => eq.active) || []}
                                    /> :
                                    <MyAppText style={styles.tableNoContentSubtitle}>{translate('noAvailableEquipments')}</MyAppText>}
                            </View>
                        </ScrollView>
                    }
                </View>
                {selectedAgentId !== 0 && unit &&
                    <EditAgentModal
                        isModalVisible={editAgentModal}
                        selectedAgentId={selectedAgentId}
                        setModalVisible={setEditAgentModal}
                        unit={unit}
                        setUnit={setUnit}
                        onConfirm={setActiveUnit}
                        type={activationType}
                    />
                }
                {selectedEquipmentId !== 0 && unit &&
                    <EditEquipmentModal
                        isModalVisible={editEquipmentModal}
                        selectedEquipmentId={selectedEquipmentId}
                        setModalVisible={setEditEquipmentModal}
                        unit={unit}
                        setUnit={setUnit}
                        onConfirm={setActiveUnit}
                    />
                }
                <FinishUnitModal
                    finishEarlyReason={finishEarlyReasonId}
                    setFinishEarlyReason={setFinishEarlyReasonId}
                    isModalVisible={finishModal}
                    setModalVisible={setFinishModal}
                    unitId={unitId}
                    needFinishReason={needEarlyFinishReason}
                    onSubmit={resetUnit}
                    onClose={() => {
                        setFinishEarlyReasonId(0);
                        setNeedEarlyFinishReason(false);
                        setDailyPoliceLog('');
                        setObservations('');
                    }}
                    dailyPoliceLog={dailyPoliceLog}
                    setDailyPoliceLog={setDailyPoliceLog}
                    observations={observations}
                    setObservations={setObservations}
                />
                {unit && <OperationalPauseModal
                    isModalVisible={operationalPauseModal}
                    setModalVisible={setOperationalPauseModal}
                    pauseReason={pauseReason}
                    setPauseReason={setPauseReason}
                    onClose={() => {
                        setOperationalPauseModal(false);
                        getPageInformation();
                    }}
                    unit={unit}
                />}
                {unit && unitRoleModal && (
                    <ChangeUnitRoleModal
                        isModalVisible={unitRoleModal !== null}
                        setModalVisible={(visible) => {
                            if (!visible) {
                                setUnitRoleModal(null);
                            }
                        }}
                        role={unitRoleModal}
                        unit={unit}
                        onConfirm={async (userId) => {
                            try {
                                await setActiveUnit(
                                    unitRoleModal === 'driver' ? { driverId: userId } : { personInChargeId: userId }
                                );
                                return true;
                            } catch (err) {
                                return false;

                            }
                        }}
                    />
                )}
            </View>
            {alreadyActive && <FloatingButton
                actions={[
                    ...(isPersonInCharge && unit?.unitName.unitCategory.hasPlate
                        ? [{
                            name: 'changeDriver',
                            label: translate('changeDriver'),
                            icon: faCar,
                            onPress: () => setUnitRoleModal('driver'),
                        }]
                        : []),
                    ...(isPersonInCharge
                        ? [{
                            name: 'changePersonInCharge',
                            label: translate('changePersonInCharge'),
                            icon: faUserGear,
                            onPress: () => setUnitRoleModal('responsible'),
                        }]
                        : []),
                    {
                        name: 'finishUnit',
                        icon: faUsersSlash,
                        label: translate('FINISH_UNIT_FOR_AGENT'),
                        onPress: () => checkResetUnit(),
                        disabled: !alreadyActive
                    },
                    ...(authenticatedUser && authenticatedUser.permissions.app_agent_pause_unit
                        ? [{
                            name: 'pauseAction',
                            label: unit?.paused ? translate('unpause') : translate('pause'),
                            icon: unit?.paused ? faPlay : faPause,
                            color: theme.colors.emphasisBlue,
                            disabled: !alreadyActive,
                            onPress: () => {
                                unit?.paused ? unpauseUnit() : setOperationalPauseModal(true);
                            },
                        }]
                        : [])
                ]}
            />}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    divider: {
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.primaryButton.background + '33'
    },
    container: {
        flexDirection: 'row',
        gap: 10,
        justifyContent: 'flex-end',
        paddingBottom: 8,
    },
    groupContainer: {
        flex: 1,
        rowGap: 16,
        marginTop: 16,
        padding: 16,
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        backgroundColor: theme.colors.container.background,
    },
    pageHeader: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.primaryButton.background + '33'
    },
    row: {
        flexDirection: 'column',
        columnGap: 30,
        justifyContent: 'flex-end'
    },
    cardList: {
        flex: 1,
    },
    lateActivatedBox: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#B8401B',
        backgroundColor: theme.colors.container.background,
        padding: 10,
        maxHeight: 100,
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10
    },
    lateActivatedText: {
        color: '#B8401B',
        fontSize: 16,
        fontWeight: 'bold'
    },
    tableCard: {
        flexDirection: 'column',
        rowGap: 8,
    },
    tableHeader: (width: number) => ({
        width: width,
        fontSize: 14,
        fontWeight: '400',
        color: theme.colors.dataTable.rowText
    }),
    tableRowContent: {
        fontSize: 14,
        fontWeight: '400',
        color: theme.colors.primaryButton.background
    },
    tableTitle: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.primaryButton.background,
        textTransform: 'uppercase'
    },
    tableNoContentSubtitle: {
        fontSize: 12,
        fontWeight: '400',
        color: theme.colors.modal.subheader
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center'
    },
    circle: {
        width: 8,
        height: 8,
        borderRadius: 7.5,
    },
    title: {
        fontWeight: '600',
        fontSize: 16,
        color: theme.colors.neutral['500']
    },
    subTitle: {
        fontWeight: '400',
        fontSize: 14,
        color: theme.colors.neutral['400']
    }
}));
